/**
 * The host role: answer what an asset reports, and do to it what a host can do.
 *
 * Everything an asset can ask of a host arrives in `callbacks()`, and everything the host can do to
 * an asset — Check, Submit, Reset — leaves from here. What it does NOT do is mount: where the frame
 * goes, what happens when an asset never says hello, and how a superseded mount is told apart from
 * the current one are each host's own chrome, so the caller passes `callbacks()` to `embed()` and
 * hands the resulting `Player` back through `player`.
 *
 * It says what happened through `hooks.note`, in sentences the studio's end-to-end suite asserts on.
 * Change one and the suite says so.
 */

import type { AnswerReport, EmbedOptions, HostInit, Player } from '@cet/asset-host';
import { score, scoreItem } from '@cet/asset-scoring';
import { AI_SERVICES, isJudged } from '@cet/asset-contract';
import type { ErrorPayload, HintPayload, ItemVerdict, Judgement, PagePayload, SchemeDoc, Score, ToolsPayload,
  AiPayload,
  AiService,
  ExternalPayload,
  ExternalService,
  ExternalStatus,
} from '@cet/asset-contract';

import {
  FEEDBACK_LABEL,
  clearAttempt,
  clearItem as clearItemFromRecord,
  closedBecause,
  isReadOnly,
  scoreForInit,
  solutionForInit,
  solutionOffer,
  triesLimit,
  verdictsForInit,
  wireFeedbackMode,
  wireLocale,
} from './policy.js';
import type { HostRecord } from './policy.js';

/** What a host is told, and what it is asked to do that only its own chrome can. */
export interface SessionHooks {
  /** One line for the event trace. */
  note(line: string): void;
  /** The record changed; redraw whatever shows it. */
  changed(): void;
  /** A setting changed that only a fresh `init` can carry — a granted locale request. */
  remount(why: string): void;
  /** Re-initialise the frame after a submit, which closes the attempt (D50). */
  mount(): Promise<void>;
  /** A score request started or settled. `problem` is what went wrong, for the score panel. */
  scoring(inFlight: boolean, problem: string | null): void;
  /** The payload that was just built, for an inspector. */
  initBuilt?(init: HostInit): void;
  fullscreen?(): void;
  resized?(height: number): void;
  progress?(fraction: number): void;
  /** Where the learner is in a paged activity (D65), for a host drawing page controls. */
  page?(payload: PagePayload): void;
  /** The asset's tools for the host's bar (D98), for a host that draws them. */
  tools?(payload: ToolsPayload): void;
  /**
   * An answer the host recorded, with the verdict it got as it arrived — under `onAnswer` only,
   * judged here from the scheme the host holds, since the asset's own mark never reaches the host.
   */
  answered?(report: AnswerReport, verdict: ItemVerdict | null): void;
  /** A hint the learner was given (D48), as recorded. */
  hinted?(payload: HintPayload): void;
  /** Marks released to the asset at a Check. */
  checked?(verdicts: readonly ItemVerdict[]): void;
  /** A question's answer was shown, because the learner asked for it (D66). */
  solutionShown?(itemId: string): void;
  /** The record was cleared: whoever asked, a new attempt starts here. */
  reset?(): void;
  /** One question was cleared from the record — the same attempt goes on. */
  itemCleared?(itemId: string): void;
  /** The server's result for the submit that ended the attempt. */
  submitted?(result: { total: Score; parts?: Record<string, Score>; perItem: ItemVerdict[]; scoringVersion: string }): void;
  done?(): void;
  error?(payload: ErrorPayload): void;
  /**
   * The asset asks to open, move or close an external activity (D78). Open it in the host's own
   * page and call `reply` as it goes — `open`, then `finished` or `closed`. Absent: `unavailable`.
   */
  external?(payload: ExternalPayload, reply: (status: ExternalStatus) => void): void;
}

export interface SessionOptions {
  /** The record this session reads and writes. Held by the caller, so its own chrome can read it. */
  readonly record: HostRecord;
  /** `POST /v1/score`, absolute or same-origin. */
  readonly scoreUrl: string;
  /** What a failed score request names when it says the service is unreachable. */
  readonly scoreLabel?: string;
  readonly hooks: SessionHooks;
  /** The grading rules the host was handed, or null when it holds none. */
  scheme(): SchemeDoc | null;
  /** `manifest.locales` of the asset in the frame. */
  locales(): readonly string[];
  /** One retry when a submit finds no scheme held — the commonest cause is a restart mid-load. */
  reloadScheme?(): Promise<void>;
  /**
   * Where the host's server scores against its own copy of the scheme, when this page holds none on
   * purpose (D75): `POST {responses, locale}`, the same answer `/v1/score` gives. Null or absent means
   * the page scores with the scheme it holds.
   */
  heldScoreUrl?(): string | null;
  /**
   * True when this host draws the page controls (D65), so `init` tells the asset to hide its own.
   * Absent is false: the asset keeps its Back and Next, as under every host that predates it.
   */
  hostNavigation?(): boolean;
  /** True when this host draws Check for the page on screen (D98). Absent is false. */
  hostCheck?(): boolean;
  /** True when this host draws the asset's tools on its bar (D98). Absent is false. */
  hostTools?(): boolean;
  /**
   * Whether a submitted attempt may be thrown away and started again, when the asset asks. Absent is
   * true — Reset after a submit is the way to a new attempt (D49). A host whose submit is final (an
   * evaluation) says false, and the attempt stays closed: its questions and marks can be looked at,
   * never answered again.
   */
  canStartAgain?(): boolean;
  /**
   * After how many checks a question may show its answer, or `null` for never (D66) — the
   * product's rule, from the player's provider. Absent is never.
   */
  solutionAfter?(): number | null;
  /** After how many checks a question is closed, or `null` for never (D97). Absent is never. */
  maxTries?(): number | null;
  /** Whether a learner may press Check before answering — the provider's rule. Absent is no. */
  checkUnanswered?(): boolean;
  /** The external activities this host can open (D78) — offered in `init`. Absent: none. */
  externalServices?(): readonly ExternalService[];
  /**
   * Answers the asset's AI requests (D86) — the studio with the mock, the player through its server.
   * Resolve to the answer or null. Absent: no AI offered.
   */
  ai?(payload: AiPayload): Promise<unknown>;
  /** Which AI services `ai` answers. Absent: all of them. */
  aiServices?(): readonly AiService[];
  /**
   * Judges an open answer (D93) — the player through its server, the studio with the mock. Resolve
   * to the judgement, or null when there is none: the answer is then not judged, never marked
   * wrong. Absent: nothing is judged in the page, and a held score judges at the submit.
   */
  judge?(itemId: string, text: string): Promise<Judgement | null>;
  /** The launcher's learner id, sent with a held score so the server's judging is theirs (D86). */
  learnerRef?(): string | null;
}

/** Everything `embed()` takes that is about the conversation rather than the frame. */
export type SessionCallbacks = Omit<EmbedOptions, 'container' | 'src' | 'title' | 'mountTimeoutMs'>;

const timeoutSignal = (ms: number): AbortSignal | null =>
  typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal ? AbortSignal.timeout(ms) : null;

export class HostSession {
  /** The mounted asset, set by the caller once `embed()` resolves and cleared when it goes. */
  player: Player | null = null;

  constructor(private readonly options: SessionOptions) {}

  get record(): HostRecord {
    return this.options.record;
  }

  private get hooks(): SessionHooks {
    return this.options.hooks;
  }

  feedback = () => wireFeedbackMode(this.record);
  /** `init.solutionAfter` for this record, or null (D66). */
  offer = (): number | null =>
    solutionOffer(this.record, this.options.solutionAfter?.() ?? null, this.options.scheme());
  /** `init.maxTries` for this record, or null (D97). */
  limit = (): number | null =>
    triesLimit(this.record, this.options.maxTries?.() ?? null, this.options.scheme());
  /** The question has been checked as often as the limit allows: closed to answers and resets. */
  outOfTries = (itemId: string): boolean => {
    const limit = this.limit();
    return limit !== null && (this.record.checks[itemId] ?? 0) >= limit;
  };
  locale = (): string => wireLocale(this.record, this.options.locales());
  readOnly = (): boolean => isReadOnly(this.record);
  closedBecause = (): string => closedBecause(this.record);

  /** The payload for the next mount, built from the record and nothing else. */
  init(): HostInit {
    const record = this.record;
    const feedback = this.feedback();
    const scheme = this.options.scheme();
    const init: HostInit = {
      mode: record.mode,
      // Spread rather than assigned, because "not sent" is the fact under review
      // (`exactOptionalPropertyTypes`), and a key holding `undefined` is not the same wire.
      ...(feedback === null ? {} : { feedbackMode: feedback }),
      locale: this.locale(),
      canWrite: !this.readOnly(),
      // null → the SDK fetches the asset's own content.json. That is the
      // bundled-content case, and the asset cannot tell it from a
      // platform-stored or generated instance (D11).
      content: null,
      // The whole exam guarantee, as one nullable field. Note solution mode does NOT
      // relax it: the answers go over as `solution` below, and the grading rules that
      // produced them stay on this side.
      scheme: record.mode === 'practice' ? scheme : null,
      // The answers, in solution mode and nowhere else (see `solutionForInit`).
      solution: solutionForInit(record, scheme),
      state: record.state,
      // Sent only when true, so a host that does not draw page controls puts nothing new on the wire.
      ...(this.options.hostNavigation?.() === true ? { hostNavigation: true } : {}),
      ...(this.options.hostCheck?.() === true ? { hostCheck: true } : {}),
      ...(this.options.hostTools?.() === true ? { hostTools: true } : {}),
      // Sent only when offered: an older asset ignores it, and absent already means never.
      ...(this.offer() === null ? {} : { solutionAfter: this.offer() as number }),
      ...(this.limit() === null ? {} : { maxTries: this.limit() as number }),
      // Sent only when true, as hostNavigation is: absent already means "Check waits for an answer".
      ...(this.options.checkUnanswered?.() === true ? { checkUnanswered: true } : {}),
      // Sent only when there is one, as the other offers are: absent already means none.
      ...(this.externalOffer().length > 0 ? { externalServices: this.externalOffer() } : {}),
      ...(this.aiOffer().length > 0 ? { aiServices: this.aiOffer() } : {}),
      verdicts: verdictsForInit(record, this.locale()),
      score: scoreForInit(record),
    };
    this.hooks.initBuilt?.(init);
    return init;
  }

  callbacks(): SessionCallbacks {
    const { hooks } = this;
    const note = (line: string): void => hooks.note(line);

    return {
      init: () => this.init(),

      onSave: (state) => {
        // A submitted attempt is the record, not a draft. Refused out loud rather than
        // silently dropped: an asset that saves over a closed attempt is a bug in the asset,
        // and the trace is where its author will look.
        if (this.readOnly()) {
          note(`saveState REFUSED — ${this.closedBecause()} (canWrite: false)`);
          return;
        }
        this.record.state = state;
        note('saveState');
        hooks.changed();
      },

      onAnswer: (report) => {
        if (this.readOnly()) {
          note(`answer ${report.itemId} REFUSED — ${this.closedBecause()} (canWrite: false)`);
          /**
           * A refused answer gets NO verdict back. The stored verdict is not a mark for *this*
           * answer, it is the mark for the one the host still holds — handing it back let a learner
           * in a review change a correct answer to a wrong one and watch the asset stamp it
           * correct. A replayed attempt gets its marks from `init.verdicts`; an `answer()` reply
           * was never the route by which a review received a mark.
           */
          return null;
        }
        if (this.outOfTries(report.itemId)) {
          note(`answer ${report.itemId} REFUSED — checked ${this.limit()} time(s), the most allowed`);
          return null;
        }
        // Stored SEPARATELY from the state blob. This is the host obligation the
        // whole scoring model rests on: if answers live only in the blob, the host
        // cannot score them.
        this.record.responses[report.itemId] = report.response;
        // Under `onAnswer` an answer IS a check: the asset marked it as it arrived (D66).
        if (this.feedback() === 'onAnswer') this.countCheck(report.itemId);
        note(`answer ${report.itemId} ${JSON.stringify(report.response)}`);
        const settle = (judged: ItemVerdict | null): ItemVerdict | null => {
          // A newer answer came while this one was judged: the mark is for that one, in its own reply.
          if (this.record.responses[report.itemId] !== report.response) return null;
          // The mark this answer was shown with, kept for a re-mount (D70) — or none, for a new
          // answer not marked yet (the old mark was for the answer before).
          this.release(report.itemId, judged);
          hooks.answered?.(report, judged);
          hooks.changed();
          // An open answer has only this mark: the asset cannot make one (D93). Exam mode has not
          // been scored yet — Submit does that. After a submit, a re-answer gets the stored verdict.
          if (judged !== null && isJudged(this.options.scheme()?.items[report.itemId])) return judged;
          return this.record.verdicts?.find((verdict) => verdict.itemId === report.itemId) ?? null;
        };
        // Synchronous unless an open answer is being judged.
        const arrival = this.judgeOnArrival(report);
        return arrival instanceof Promise ? arrival.then(settle) : settle(arrival);
      },

      onResize: (height) => hooks.resized?.(height),

      /**
       * A hint was given (D48). Recorded, because that is the whole point of the message: a real
       * host stores it beside the responses. In exam mode **nothing should appear here at all** —
       * the scheme is withheld, so the asset has no hint to reveal and cannot report one.
       */
      onHint: (payload) => {
        this.record.hintsUsed.push(payload);
        note(`hint ${payload.itemId} "${payload.hintId}" (#${payload.index})`);
        hooks.hinted?.(payload);
        hooks.changed();
      },

      /**
       * The asset asked for another language, and the host is what changes it — the same act as
       * a host's own locale menu: record the choice and re-initialise. Refused outside
       * `manifest.locales`, which is the promise a host must keep.
       */
      onLocale: ({ locale }) => {
        if (!this.options.locales().includes(locale)) {
          note(`locale → ${locale} refused · the asset does not declare it`);
          return;
        }
        if (locale === this.record.locale) return;
        this.record.locale = locale;
        hooks.remount(`the asset asked for ${locale} · the host re-inits`);
      },

      /**
       * The asset asked to start again. Refused in review and solution, where there is no attempt
       * of this session's to discard.
       */
      onReset: ({ itemId }) => {
        if (this.record.mode === 'review') {
          note('reset refused · a review is a finished attempt, and the host will not discard it');
          return;
        }
        if (this.record.mode === 'solution') {
          note('reset refused · the solution is being shown; there is no attempt to start again');
          return;
        }
        if (itemId !== undefined) {
          if (this.outOfTries(itemId)) {
            note(`reset ${itemId} refused · checked ${this.limit()} time(s), the most allowed`);
            return;
          }
          this.clearItem(itemId, 'the asset');
          return;
        }
        if (this.record.submitted && this.options.canStartAgain?.() === false) {
          note('reset refused · this attempt was submitted, and it is final');
          return;
        }
        this.reset('the asset');
      },

      /**
       * The asset's own Check control, answered by the same function a host's toolbar calls. The
       * scope is the only difference: an asset asks about the question in front of the learner.
       */
      onCheckRequest: ({ itemId }) => {
        if (this.readOnly()) {
          note(`check refused · ${this.closedBecause()}`);
          return;
        }
        note(`asset requested check${itemId === undefined ? '' : ` · ${itemId}`}`);
        this.check(itemId);
      },

      /**
       * The learner asked to see one question's answer (D66). Honoured only as offered: the attempt
       * open, marks given in this session, and this question checked as many times as
       * `solutionAfter` says — counted here, never taken from the asset.
       */
      onSolutionRequest: ({ itemId }) => this.showSolution(itemId),

      /**
       * An external activity (D78). Not in a finished attempt or a solution: nothing is being done
       * there, so nothing opens — the asset is told `unavailable` and shows its step as it is.
       */
      onExternal: (payload) => {
        const reply = (status: ExternalStatus): void => this.player?.external({ key: payload.key, status });
        if (payload.action === 'open' && (this.readOnly() || !this.externalOffer().includes(payload.service) || !hooks.external)) {
          note(`external ${payload.service} "${payload.key}" — not opened (${this.readOnly() ? this.closedBecause() : 'not offered'})`);
          reply('unavailable');
          return;
        }
        if (payload.action !== 'layout') note(`external ${payload.action} ${payload.service} "${payload.key}"`);
        hooks.external?.(payload, reply);
      },

      /** The AI (D86): only as offered, and never a reason to fail — any trouble answers null. */
      onAi: async (payload) => {
        const ask = this.options.ai;
        if (ask === undefined || !this.aiOffer().includes(payload.service)) {
          note(`ai ${payload.service} · ${payload.itemId} — not answered (${this.readOnly() ? this.closedBecause() : 'not offered'})`);
          return null;
        }
        try {
          const answer = (await ask(payload)) ?? null;
          note(`ai ${payload.service} · ${payload.itemId}${answer === null ? ' — no answer' : ''}`);
          return answer;
        } catch (error) {
          note(`ai ${payload.service} · ${payload.itemId} — failed: ${error instanceof Error ? error.message : String(error)}`);
          return null;
        }
      },

      onFullscreen: () => hooks.fullscreen?.(),

      onProgress: (fraction) => {
        note(`progress ${Math.round(fraction * 100)}%`);
        hooks.progress?.(fraction);
      },
      onPage: (payload) => {
        const lock = payload.canAdvance === false ? ' · forward locked' : '';
        note(`page ${payload.index + 1} of ${payload.count}${lock}`);
        hooks.page?.(payload);
      },
      onTools: (payload) => {
        note(`tools · ${payload.tools.map((tool) => tool.id).join(', ') || 'none'}`);
        hooks.tools?.(payload);
      },
      onDone: () => {
        note('complete — the asset says it is finished');
        hooks.done?.();
      },
      onError: (payload) => {
        hooks.error?.(payload);
        note(`${payload.fatal ? 'FATAL' : 'error'}: ${payload.message}`);
      },
    };
  }

  /**
   * The verdict an answer got as it arrived, which only `onAnswer` gives one.
   *
   * The asset marked itself from the delivered scheme and kept the mark: nothing in the protocol
   * sends it back. So a host that wants to record whether the answer was right judges it the same
   * way, with the same scorer and the same scheme — the parity the whole scoring model rests on.
   */
  private judgeOnArrival(report: AnswerReport): ItemVerdict | null | Promise<ItemVerdict | null> {
    if (this.feedback() !== 'onAnswer') return null;
    const itemScheme = this.options.scheme()?.items[report.itemId];
    if (itemScheme === undefined) return null;
    const verdict = scoreItem(report.itemId, itemScheme, report.response, this.locale(), this.record.judgements[report.itemId]);
    // An exact answer needs no judging; only an open one the scorer could not mark (D93).
    if (verdict.correct !== null || !isJudged(itemScheme)) return verdict;
    return this.judged(report.itemId).then((judgement) =>
      scoreItem(report.itemId, itemScheme, report.response, this.locale(), judgement));
  }

  /**
   * The judgement of a question's current answer (D93): the one held when it is for this text, else
   * asked for now and kept. Undefined when nothing judges here or the judging failed.
   */
  private async judged(itemId: string): Promise<Judgement | undefined> {
    const response = this.record.responses[itemId];
    if (response === undefined || !('text' in response)) return undefined;
    const held = this.record.judgements[itemId];
    if (held?.text === response.text) return held;
    const judge = this.options.judge;
    if (judge === undefined) return undefined;
    try {
      const judgement = await judge(itemId, response.text);
      if (judgement === null || judgement.itemId !== itemId || judgement.text !== response.text) {
        this.hooks.note(`judge ${itemId} — no judgement`);
        return undefined;
      }
      this.record.judgements = { ...this.record.judgements, [itemId]: judgement };
      this.hooks.note(`judge ${itemId} → ${judgement.points}/${judgement.max}`);
      return judgement;
    } catch (error) {
      this.hooks.note(`judge ${itemId} failed: ${error instanceof Error ? error.message : String(error)}`);
      return undefined;
    }
  }

  /** The answered open questions the scorer cannot mark yet — all, or the one `only` names (D93). */
  private unjudged(scheme: SchemeDoc, only?: string): string[] {
    const locale = this.locale();
    return Object.entries(scheme.items)
      .filter(([itemId, item]) => {
        const response = this.record.responses[itemId];
        return (only === undefined || itemId === only) && response !== undefined && isJudged(item)
          && scoreItem(itemId, item, response, locale, this.record.judgements[itemId]).correct === null;
      })
      .map(([itemId]) => itemId);
  }

  /** Judge them, if this host judges at all. */
  private async judgeAnswered(scheme: SchemeDoc, only?: string): Promise<void> {
    if (this.options.judge === undefined) return;
    await Promise.all(this.unjudged(scheme, only).map((itemId) => this.judged(itemId)));
  }

  /**
   * Move between pages, for a host that draws page controls (D65). The asset moves and reports the
   * page it landed on; whether forward is open is the asset's rule, so the host only asks.
   */
  navigate(direction: 'next' | 'back'): void {
    if (this.player === null) return;
    this.player.command(direction);
    this.hooks.note(`navigate → ${direction}`);
  }

  /**
   * The host's Check for the page on screen (D98): the asset checks its question as its own Check
   * would, and asks for the mark with a scoped request, which `check(itemId)` answers.
   */
  checkPage(): void {
    if (this.player === null) return;
    if (this.readOnly()) {
      this.hooks.note(`check refused · ${this.closedBecause()}`);
      return;
    }
    this.player.command('check');
    this.hooks.note('check → the page on screen');
  }

  /** A tool on the host's bar was pressed (D98); what it does is the asset's. */
  pressTool(id: string): void {
    if (this.player === null) return;
    this.player.tool(id);
    this.hooks.note(`tool → ${id}`);
  }

  /**
   * A Check is feedback, not a submission.
   *
   * In `onAnswer` the asset marks itself from the delivered scheme, so this is only a nudge
   * towards whatever is still unfinished. In `onCheck` **nothing inside the frame can mark
   * anything** — `answer()` resolves `null` by design — so the marks have to come from the host.
   *
   * Scored in this browser rather than through `/v1/score`, because a Check is not the record:
   * the server recomputes at submit and that number is the one that counts.
   *
   * `itemId` scopes the Check to one question, which is what an asset's own Check control sends
   * (D57). Undefined is the whole activity, which is what a host's toolbar button sends.
   */
  check(itemId?: string): void | Promise<void> {
    const note = (line: string): void => this.hooks.note(line);

    // Only for a whole-activity Check: the command means "say what is still unanswered", which is
    // a statement about the activity, and after a one-question request it moves the learner away
    // from the mark they asked for.
    if (itemId === undefined) this.player?.command('check');

    // The declared timing decides, and the mode is not consulted (D49).
    const feedback = this.feedback();
    if (feedback !== 'onCheck') {
      note(
        `feedback is ${feedback === null ? 'not sent' : FEEDBACK_LABEL[feedback]}, ` +
          `so a Check reveals no marks — it only asks the asset what is still unanswered`,
      );
      return;
    }
    const scheme = this.options.scheme();
    if (scheme === null) {
      note('no scheme held; nothing to check against');
      return;
    }

    // Synchronous unless an open answer is judged first (D93).
    if (this.options.judge !== undefined && this.unjudged(scheme, itemId).length > 0) {
      return this.judgeAnswered(scheme, itemId).then(() => this.mark(scheme, itemId));
    }
    this.mark(scheme, itemId);
  }

  /** A Check's marks: every answered question in scope, released to the asset. */
  private mark(scheme: SchemeDoc, itemId: string | undefined): void {
    const note = (line: string): void => this.hooks.note(line);
    const responses = this.record.responses;
    const result = score({ scheme, responses, locale: this.locale(), judgements: this.record.judgements });
    // Only the items that were actually answered — a Check is a pause mid-activity, and marking
    // every untouched question wrong is noise rather than feedback. The scope is honoured, not
    // treated as a hint: revealing the rest would mark work the learner did not ask about.
    // A question out of tries keeps the mark it was closed with: checking it again is refused (D97).
    if (itemId !== undefined && this.outOfTries(itemId)) {
      note(`check → ${itemId} refused · checked ${this.limit()} time(s), the most allowed`);
      return;
    }
    const answered = result.perItem.filter(
      (verdict) =>
        responses[verdict.itemId] !== undefined && (itemId === undefined || verdict.itemId === itemId)
        && !this.outOfTries(verdict.itemId),
    );
    if (answered.length === 0) {
      note(
        itemId === undefined
          ? 'check → nothing answered yet, so nothing to mark'
          : `check → ${itemId} is not answered yet, so there is nothing to mark`,
      );
      return;
    }

    // No `score` in the payload: a total over the answered subset is not this activity's total.
    for (const verdict of answered) {
      this.countCheck(verdict.itemId);
      this.release(verdict.itemId, verdict);
    }
    this.player?.verdicts({ verdicts: answered });
    this.hooks.checked?.(answered);
    note(
      itemId === undefined
        ? `check → verdicts (${answered.length} of ${result.perItem.length} item(s) answered)`
        : `check → verdicts (${itemId} only)`,
    );
  }

  /** Keep the mark a question was just shown with, or drop it (null) — see `HostRecord.released`. */
  private release(itemId: string, verdict: ItemVerdict | null): void {
    const { [itemId]: _old, ...rest } = this.record.released;
    this.record.released = verdict === null ? rest : { ...rest, [itemId]: { verdict, locale: this.locale() } };
  }

  /** What this host offers the asset to open (D78) — none in a finished attempt or a solution. */
  private externalOffer(): readonly ExternalService[] {
    return this.readOnly() ? [] : (this.options.externalServices?.() ?? []);
  }

  /** What the host's AI answers (D86), in an open practice attempt; nothing in an exam. */
  private aiOffer(): readonly AiService[] {
    if (this.options.ai === undefined || this.record.mode !== 'practice' || this.readOnly()) return [];
    return this.options.aiServices?.() ?? AI_SERVICES;
  }

  private countCheck(itemId: string): void {
    this.record.checks[itemId] = (this.record.checks[itemId] ?? 0) + 1;
  }

  /**
   * Show one question's answer, when the learner asked and the host offered it (D66).
   *
   * The answer comes from the scheme this host holds, through the same `solutionFor()` a solution
   * session uses, so there is one definition of what is right. Recorded, as a hint is: it is the
   * difference between a question a learner solved and one whose answer they were shown.
   */
  showSolution(itemId: string): void {
    const note = (line: string): void => this.hooks.note(line);
    const after = this.offer();
    if (after === null) {
      note(`solution ${itemId} refused · ${this.readOnly() ? this.closedBecause() : 'this session does not offer solutions'}`);
      return;
    }
    const checks = this.record.checks[itemId] ?? 0;
    if (checks < after) {
      note(`solution ${itemId} refused · checked ${checks} time(s), and the answer is offered after ${after}`);
      return;
    }
    const answer = solutionForInit({ mode: 'solution' }, this.options.scheme())?.[itemId];
    if (answer === undefined) {
      note(`solution ${itemId} refused · the scheme has no answer for it`);
      return;
    }
    if (!this.record.solutionsShown.includes(itemId)) this.record.solutionsShown.push(itemId);
    this.player?.solution({ solution: { [itemId]: answer } });
    this.hooks.solutionShown?.(itemId);
    note(`solution ${itemId} shown · after ${checks} check(s)`);
    this.hooks.changed();
  }

  /**
   * Submit ends the attempt, scores it, and releases the marks unless the timing says otherwise.
   * All three are one act (D49). Resolves with the server's result, or null when nothing was scored.
   */
  async submit(): Promise<{ total: Score; perItem: ItemVerdict[]; scoringVersion: string } | null> {
    const { hooks, record } = this;
    const note = (line: string): void => hooks.note(line);

    // A closed attempt is the record. Re-scoring it would overwrite the marks it already carries.
    if (this.readOnly()) {
      note(`submit REFUSED — ${this.closedBecause()}`);
      return null;
    }

    const heldScoreUrl = this.options.heldScoreUrl?.() ?? null;
    if (heldScoreUrl === null && this.options.scheme() === null && this.options.reloadScheme !== undefined) {
      note('no scheme held; retrying the load before scoring');
      await this.options.reloadScheme();
      hooks.changed();
    }
    const scheme = heldScoreUrl === null ? this.options.scheme() : null;
    if (heldScoreUrl === null && scheme === null) return null;

    const locale = this.locale();
    // The open answers this page can judge, before the score is asked for (D93). A held score is
    // judged by the server, which takes a judgement from here only when it signed it.
    if (scheme !== null) await this.judgeAnswered(scheme);
    const judgements = record.judgements;
    const learnerRef = this.options.learnerRef?.() ?? null;
    // Sent only when there are some, so an attempt with no open answer puts nothing new on the wire.
    const extra = {
      ...(Object.keys(judgements).length > 0 ? { judgements } : {}),
      ...(heldScoreUrl !== null && learnerRef !== null ? { learnerRef } : {}),
    };
    const scoreUrl = heldScoreUrl ?? this.options.scoreUrl;
    // What the trace calls the request: the pure scorer, or the host's server scoring its own copy.
    const call = heldScoreUrl === null ? 'POST /v1/score' : `POST ${heldScoreUrl}`;
    const body = JSON.stringify(
      scheme === null
        ? { responses: record.responses, ...extra, locale }
        : { scheme, responses: record.responses, ...extra, locale },
    );

    // The same package, in this browser. Not the asset's number — the asset never sends one —
    // but the same inputs through the same code, which is the parity claim (invariant 7). A page
    // that holds no scheme has no number of its own; the server's is the only one.
    record.browserScore = scheme === null ? null : score({ scheme, responses: record.responses, locale, judgements }).total;

    // Announced before the request goes out, so the browser number appears at once and the
    // server row shows that it is waiting.
    hooks.scoring(true, null);
    hooks.changed();

    try {
      const response = await fetch(scoreUrl, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body,
        // Bounded, because a connection to a closed port can take seconds to be refused. A held
        // score may judge open answers first (D93), each up to the hub's 8 s, side by side.
        signal: timeoutSignal(heldScoreUrl === null ? 10_000 : 30_000),
      });
      if (!response.ok) {
        const detail = await response.text();
        hooks.scoring(false, `${call} returned ${response.status}.\n${detail}`);
        note(`${call} → ${response.status} ${detail}`);
        hooks.changed();
        return null;
      }
      const result = (await response.json()) as {
        total: Score;
        parts?: Record<string, Score>;
        perItem: ItemVerdict[];
        scoringVersion: string;
      };

      // What the host stores. From here on the asset is *given* the result.
      hooks.scoring(false, null);
      record.serverScore = result.total;
      record.serverParts = result.parts ?? null;
      record.verdicts = result.perItem;
      record.verdictsLocale = locale;
      record.score = result.total;
      // The attempt is over BEFORE the marks go out: releasing first would leave a window in
      // which the asset has been told which items were wrong and the host still accepts
      // corrections to them.
      record.submitted = true;
      hooks.submitted?.(result);

      note(
        `${call} → ${result.total.raw}/${result.total.max}` +
          `${result.total.passed === undefined ? '' : result.total.passed ? ' (passed)' : ' (failed)'}` +
          ` scoring ${result.scoringVersion}`,
      );
      hooks.changed();

      /**
       * Re-init, because a closed attempt is a different session (D50). `canWrite` only reaches an
       * asset at a mount, so the marks, the total and `canWrite: false` go over in one payload. The
       * declared timing still decides whether marks travel at all (D49): `verdictsForInit` withholds
       * them under `none`.
       */
      if (this.feedback() === 'none') {
        note('feedback none — the host keeps the result; nothing is released to the asset');
      } else {
        note(`verdicts → asset at init (${result.perItem.length} item(s)) · attempt closed`);
      }
      await hooks.mount();
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      hooks.scoring(
        false,
        `Could not reach ${this.options.scoreLabel ?? this.options.scoreUrl}.\n` +
          `The scoring service is down. Start it with \`npm start\`.\n` +
          `(${message})`,
      );
      note(`${call} failed: ${message}`);
      hooks.changed();
      return null;
    }
  }

  /**
   * Reset clears the asset **and the host's own record of the attempt**.
   *
   * The command alone is not enough: the asset empties itself while the host still holds every
   * answer, and a submit at that moment would score answers that no longer exist anywhere else.
   * After a submit this is also the way to a NEW attempt, and the only one (D49).
   *
   * `player.command('reset')` last, so the asset clears after the record does.
   */
  /**
   * Clear one question: its answer, marks and hints leave the record, then the asset is told to
   * clear it on screen — the host first, as with a whole reset, so nothing it no longer holds can be
   * scored.
   *
   * Refused once the attempt is closed (a submit, a review): clearing a scored answer would rewrite
   * the record (D49). "Start a new attempt" is the way back from there.
   */
  clearItem(itemId: string, source: 'the host' | 'the asset'): void {
    const player = this.player;
    if (player === null) return;
    if (this.readOnly()) {
      this.hooks.note(`clear ${itemId} refused · ${this.closedBecause()}`);
      return;
    }
    // Under a tries limit the count outlives the reset, or Reset would hand out more tries (D97).
    const checks = this.record.checks[itemId];
    clearItemFromRecord(this.record, itemId);
    if (this.limit() !== null && checks !== undefined) this.record.checks[itemId] = checks;
    this.hooks.itemCleared?.(itemId);
    player.command('reset', itemId);
    this.hooks.note(`clear ${itemId} by ${source} — its answer and marks are dropped from the record`);
    this.hooks.changed();
  }

  reset(source: 'the host' | 'the asset'): void {
    const player = this.player;
    if (player === null) return;

    const reopened = this.record.submitted;

    clearAttempt(this.record);
    this.hooks.reset?.();
    this.hooks.scoring(false, null);
    player.command('reset');

    this.hooks.note(
      reopened
        ? `reset by ${source} — the submitted attempt is discarded and a new one starts, writes accepted again`
        : `reset by ${source} — the answers are cleared, and the host drops the marks it held for them`,
    );
    this.hooks.changed();
  }
}
